/**
 * Sign-in form (S01) — phone entry, calls `identity.requestOtp`, navigates to
 * `/sign-in/otp` carrying the challengeId.
 *
 * @see prototype/index.html — S01 baseline
 * @see code-patterns.md §20 — RHF + Zod + tRPC mutation pattern
 */
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate } from "@tanstack/react-router";
import { Card, CardBody, Form, PhoneField, Submit, SubmitProbe, MetaPill } from "@flint/ui";
import { identity } from "@flint/api-contract";
import type { z } from "zod";
import { trpc } from "../../../lib/trpc.ts";
import { useOnboardingFlow } from "../lib/flow-state.ts";

type FormValues = z.infer<typeof identity.requestOtpInput>;

export function SignInForm() {
  const navigate = useNavigate();
  const setPhone = useOnboardingFlow((s) => s.setPhone);
  const setChallenge = useOnboardingFlow((s) => s.setChallenge);
  const initialPhone = useOnboardingFlow((s) => s.phone);

  const form = useForm<FormValues>({
    resolver: zodResolver(identity.requestOtpInput),
    defaultValues: { phone: initialPhone ?? "", channel: "sms" },
  });

  const requestOtp = trpc.identity.requestOtp.useMutation();

  const onSubmit = form.handleSubmit(async (values) => {
    const result = await requestOtp.mutateAsync(values);
    setPhone(values.phone);
    setChallenge(result.challengeId);
    void navigate({ to: "/sign-in/otp" });
  });

  return (
    <Form {...form}>
      <SubmitProbe>
        <form onSubmit={onSubmit} noValidate>
          <Card>
            <CardBody className="flex flex-col gap-3.5">
              <PhoneField name="phone" label="Mobile number" />
              <Submit pendingLabel="Sending code" disabled={requestOtp.isPending}>
                Continue
              </Submit>

              <div className="flex flex-wrap gap-1.5 mt-1">
                <MetaPill>GST ready</MetaPill>
                <MetaPill>WhatsApp-native</MetaPill>
                <MetaPill>14-day free trial</MetaPill>
              </div>

              <p className="text-xs leading-[16px] text-muted">
                By continuing you agree to Flint&apos;s Terms and Privacy Policy. We&apos;ll send a
                one-time code to verify your number and route you into sign-in or workspace
                creation.
              </p>

              {requestOtp.error ? (
                <p role="alert" className="text-xs text-bad">
                  {requestOtp.error.message}
                </p>
              ) : null}
            </CardBody>
          </Card>
        </form>
      </SubmitProbe>
    </Form>
  );
}
